import {
    LeftOutlined, PhoneOutlined, UserOutlined, EnvironmentOutlined,
} from '@ant-design/icons';
import {
    Drawer, Collapse, Button, Divider,
} from 'antd';
import React from 'react';
import Loader from 'components/common/Loader';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import StatusPrettyName from './status-pretty-name';
import { STATUS_COLOR_MAP } from './consignments.helper';
import ConsignmentDetailsInternational from './consignment-details-international-new';
import { HocOptions } from 'components/common/generic-hoc.types';
import { ReduxStore } from 'reducers/redux.types';
import GenericHoc from 'components/common/generic-hoc';
import ConsignmentTracking from './consignment-tracking';
import ConsignmentsIcon from 'assets/consignments';
import LocationIcon from 'assets/LocationIcon';

const addressTypes: any = ['sender', 'destination'];
const addressMapping: any = {
    sender: 'CONSIGNOR',
    destination: 'CONSIGNEE',
};

const styles = () => ({
    drawer: {
        '& .ant-drawer-body': {
            padding: 10,
            backgroundColor: '#f9fafb',
        },
        '& .ant-drawer-title': {
            fontWeight: 700,
        },
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
    },
    drawerHeader: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        padding: 0,
        justifyContent: 'space-between',
        marginLeft: '6px',
        marginBottom: '20px',
    },
    main: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
        gap: 8,
        height: '100%',
        overflowY: 'auto',
    },
    consignmentHeader: {
        fontSize: 20,
        fontWeight: 700,
    },
    box: {
        backgroundColor: '#fff',
        borderRadius: 15,
        padding: 15,
        boxShadow: '0 2px #eee',
    },
    eddContainer: {
        width: 'calc(100% - 50px)',
        margin: '10px 0',
    },
    grayText: {
        color: '#999',
        textTransform: 'uppercase',
        fontSize: 12,
    },
    boldText: {
        fontWeight: 700,
    },
    edd: {
        fontSize: 22,
        fontWeight: 700,
    },
    quickDetails: {
        width: '100%',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
    },
    quickDetail: {
        width: 'calc(50% - 20px)',
        display: 'flex',
        flexDirection: 'column',
    },
    textBlock: {
        borderRadius: 6,
        padding: '2px 10px',
        fontWeight: 700,
        fontSize: 14,
        textAlign: 'center',
        display: 'inline-block',
        height: 'fit-content',
    },
    collapseSection: {
        width: '100%',
        padding: 0,
        '& .ant-collapse': {
            border: 'none',
        },
        '& .ant-collapse-item': {
            border: 'none !important',
        },
        '& .ant-collapse-content': {
            border: 'none',
        },
        '& .ant-collapse-header': {
            fontWeight: 700,
            display: 'flex',
            flexDirection: 'row-reverse',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#fff',
            borderBottom: '1px solid #eee',
            fontSize: 16,
            borderRadius: '15px 15px 0 0',
        },
    },
    primaryDetails: {
        padding: 10,
    },
    tracking: {
        width: '100%',
    },
    boxHeader: {
        fontSize: 16,
        fontWeight: 700,
        paddingBottom: 10,
        borderBottom: '1px solid #eee',
    },
    addressContainer: {
        backgroundColor: '#f9fafb',
        borderRadius: 15,
        padding: 15,
        display: 'flex',
        flexDirection: 'column',
    },
    addressRow: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        marginBottom: 10,
    },
    addressTimeline: {
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        padding: '10px',
        position: 'relative',
    },
    addressTimelineItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 12,
        position: 'relative',
    },
    addressTimelineIcon: {
        fontSize: 18,
        color: '#3498db',
        marginTop: 2,
    },
    addressTimelineContent: {
        width: '100%',
        backgroundColor: '#f9fafb',
        borderRadius: 15,
        padding: 15,
        display: 'flex',
        justifyContent: 'space-between',
    },
    addressTimelineLine: {
        position: 'absolute',
        margin: 0,
        left: 18,
        top: 47,
        height: 'calc(50% - 27px)',
        borderColor: '#999 !important',
        borderWidth: '0 0 0 2px !important',
    },
    purpleText: {
        color: '#9235e9',
    },
    pieceCard: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        borderTop: '1px solid #eee',
        padding: 15,
    },
    pieceRow: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
    },
});

interface ConsignmentDetailsMobileProps {
    loading: boolean;
    classes: any;
    handleGoback: () => void;
    renderPrint: () => React.ReactNode;
    consignment: any;
    currencySymbol: string;
    separateChildCN: boolean;
    tracking: any;
}

const ConsignmentDetailsMobile = (props: ConsignmentDetailsMobileProps) => {
    const {
        loading, classes, handleGoback, renderPrint, consignment,
        currencySymbol, tracking, separateChildCN,
    } = props;

    const { t } = useTranslation();

    const [primaryDetailsActive, setPrimaryDetailsActive] = React.useState<string[]>([]);
    const [showCompleteTracking, setShowCompleteTracking] = React.useState(false);
    const [addressActive, setAddressActive] = React.useState<string[]>([]);

    const statusColorObj = STATUS_COLOR_MAP[consignment.status] || {
        color: '#595959',
        background: '#fafafa',
    };

    const renderHeader = () => {
        if (loading) {
            return null;
        }

        return (
            <div className={classes.header}>
                <div className={classes.drawerHeader}>
                    <LeftOutlined
                        onClick={() => handleGoback()}
                        alt="close"
                    />
                    <div className={classes.consignmentHeader}>{consignment.reference_number}</div>
                    <div>
                        {renderPrint()}
                    </div>
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <div
                        className={classes.textBlock}
                        style={{
                            color: statusColorObj.color,
                            background: statusColorObj.background,
                            fontSize: 18,
                            padding: '6px 10px',
                        }}
                    >
                        <StatusPrettyName status={consignment.status} />
                    </div>
                    <div
                        className={classes.textBlock}
                        style={{
                            color: '#9235e9',
                            background: '#f9f1ff',
                        }}
                    >
                        {`${consignment[consignment.is_international ? 'booking_service_type_id' : 'service_type']}`}
                    </div>
                    <div
                        className={classes.textBlock}
                        style={{
                            color: consignment.is_cod ? '#389e0d' : '#999',
                            background: consignment.is_cod ? '#f6ffed' : '#ddd',
                        }}
                    >
                        {consignment.is_cod ? t('cod') : t('prepaid')}
                        {consignment.is_cod && consignment.cod_amount ? ` ${currencySymbol} ${consignment.cod_amount}` : ''}
                    </div>
                </div>
            </div>
        );
    };

    const renderEdd = () => {
        if (!consignment.expected_delivery_date) {
            return null;
        }
        return (
            <div className={`${classes.eddContainer} ${classes.box}`}>
                <div className={classes.grayText}>ESTIMATED DELIVERY</div>
                <div className={classes.edd}>{moment(consignment.expected_delivery_date).format('DD MMM, YYYY')}</div>
            </div>
        );
    };

    const renderQuickDetails = () => {
        return (
            <div className={`${classes.box} ${classes.quickDetails}`}>
                <div className={classes.quickDetail}>
                    <div className={classes.grayText}>{t('customer_reference_no')}</div>
                    <div className={classes.boldText}>{consignment.customer_reference_number || 'N/A'}</div>
                </div>
                <div className={classes.quickDetail}>
                    <div className={classes.grayText}>{t('created_at_ltl')}</div>
                    <div className={classes.boldText}>{moment(consignment.created_at).format('DD MMM YYYY')}</div>
                </div>
                <div className={classes.quickDetail}>
                    <div className={classes.grayText}>{t('pickup_attempts')}</div>
                    <div className={classes.boldText}>{consignment.pickup_attempt_count}</div>
                </div>
                <div className={classes.quickDetail}>
                    <div className={classes.grayText}>{t('delivery_attempts')}</div>
                    <div className={classes.boldText}>{consignment.attempt_count}</div>
                </div>
            </div>
        );
    };

    const renderTracking = () => {
        if (!tracking) {
            return null;
        }
        return (
            <div className={`${classes.box} ${classes.tracking}`}>
                <div className={classes.boxHeader}>
                    <LocationIcon />
                    &nbsp; Tracking Timeline
                </div>
                <ConsignmentTracking
                    tracking={tracking}
                    consignment={consignment}
                    isMobile
                    dontShowTitle
                    maxTimeline={2}
                />
                <Button
                    style={{
                        width: '100%',
                        borderRadius: 6,
                    }}
                    type="primary"
                    onClick={() => setShowCompleteTracking(!showCompleteTracking)}
                >
                    View Complete Tracking
                </Button>
            </div>
        );
    };

    const getDate = (input: string) => {
        if (!input) {
            return null;
        }
        return moment(input).format('DD MMM YYYY');
    };

    const getTime = (input: string) => {
        if (!input) {
            return null;
        }
        return moment(input).format('hh:mm A');
    };

    const renderTrackingStatus = () => {
        if (!tracking) {
            return null;
        }
        const event = tracking.find((item: any) => item.type === consignment.status);
        return (
            <div
                style={{
                    backgroundColor: statusColorObj.background,
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                    padding: 10,
                    alignItems: 'center',
                    borderRadius: 6,
                }}
            >
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ fontSize: 12, color: '#999' }}>Current Status</div>
                    <div
                        style={{
                            color: statusColorObj.color,
                            fontSize: 16,
                            fontWeight: 700,
                        }}
                    >
                        <StatusPrettyName status={consignment.status} />
                    </div>
                </div>
                <div style={{ fontSize: 12, color: '#999' }}>
                    {getDate(event.event_time || event.strActionDate)}
                    {', '}
                    {getTime(event.event_time || event.strActionTime)}
                </div>
            </div>
        );
    };

    const renderCompleteTracking = () => {
        if (!tracking || !showCompleteTracking) {
            return null;
        }
        return (
            <Drawer
                visible={showCompleteTracking}
                width="100%"
                className={classes.drawer}
                title={t('Track Consignment')}
                onClose={() => setShowCompleteTracking(false)}
            >
                <ConsignmentTracking
                    tracking={tracking}
                    consignment={consignment}
                    isMobile
                    dontShowTitle
                    renderStatus={renderTrackingStatus}
                />
            </Drawer>
        );
    };

    const renderSingleAddress = (type: string) => {
        if (!consignment[`${type}_address_line_1`] && !consignment[`${type}_address_line_2`]
            && !consignment[`${type}_address_line_3`] && !consignment[`${type}_address_line_4`]
            && !consignment[`${type}_address_line_5`]
            && !consignment[`${type}_city`] && !consignment[`${type}_state`] && !consignment[`${type}_country`]
            && !consignment[`${type}_pincode`] && !consignment[`${type}_name`] && !consignment[`${type}_phone`]
        ) {
            return null;
        }
        return (
            <div key={type} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div
                    className={classes.boldText}
                    style={{
                        color: '#3498db',
                    }}
                >
                    <UserOutlined />
                    &nbsp;
                    {addressMapping[type]}
                </div>
                <div className={classes.addressContainer}>
                    <div className={classes.addressRow}>
                        <div className={classes.boldText}>
                            {consignment[`${type}_name`] || ''}
                            {consignment[`${type}_city`] ? `, ${consignment[`${type}_city`]}` : ''}
                            {consignment[`${type}_state`] ? `, ${consignment[`${type}_state`]}` : ''}
                            {consignment[`${type}_country`] ? `, ${consignment[`${type}_country`]}` : ''}
                        </div>
                        <div className={classes.boldText}>{consignment[`${type}_pincode`]}</div>
                    </div>
                    <div>
                        {consignment[`${type}_address_line_1`]}
                    </div>
                    <div>
                        {consignment[`${type}_address_line_2`]}
                    </div>
                    <div>
                        {consignment[`${type}_address_line_3`]}
                    </div>
                    <div>
                        {consignment[`${type}_address_line_4`]}
                    </div>
                    <div>
                        {consignment[`${type}_address_line_5`]}
                    </div>
                    <div className={classes.addressRow}>
                        <div className={classes.boldText}>
                            {consignment[`${type}_name`] && <UserOutlined />}
                            &nbsp;
                            {consignment[`${type}_name`]}
                        </div>
                        {consignment[`${type}_phone`] && (
                            <div
                                className={classes.boldText}
                                style={{
                                    color: '#3498db',
                                }}
                            >
                                <PhoneOutlined rotate={90} />
                                &nbsp;
                                {consignment[`${type}_phone`]}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderTimelineAddress = (type: string, icon: React.ReactNode, iconColor: string) => {
        if (
            !consignment[`${type}_address_line_1`]
            && !consignment[`${type}_address_line_3`]
            && !consignment[`${type}_address_line_4`]
            && !consignment[`${type}_address_line_5`]
            && !consignment[`${type}_city`]
            && !consignment[`${type}_state`]
            && !consignment[`${type}_country`]
        ) {
            return null;
        }
        return (
            <div className={classes.addressTimelineItem} key={type}>
                <span
                    className={classes.addressTimelineIcon}
                    style={{ color: iconColor }}
                >
                    {icon}
                </span>
                <div className={classes.addressTimelineContent}>
                    <div>
                        {consignment[`${type}_name`] || ''}
                        {consignment[`${type}_city`] ? `, ${consignment[`${type}_city`]}` : ''}
                        {consignment[`${type}_state`] ? `, ${consignment[`${type}_state`]}` : ''}
                    </div>
                    {
                        consignment[`${type}_pincode`]
                        && (
                            <div className={classes.boldText}>
                                {consignment[`${type}_pincode`]}
                            </div>
                        )
                    }
                </div>
            </div>
        );
    };

    const renderAddress = () => {
        return (
            <div className={`${classes.box} ${classes.collapseSection}`}>
                <Collapse
                    activeKey={addressActive}
                    onChange={(keys) => setAddressActive(Array.isArray(keys) ? keys : [keys])}
                >
                    <Collapse.Panel
                        header={
                            (
                                <div>
                                    <EnvironmentOutlined />
                                    &nbsp;
                                    Address Details
                                </div>
                            )
                        }
                        key="1"
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {
                                addressTypes.map((type: any) => (
                                    renderSingleAddress(type)
                                ))
                            }
                        </div>
                    </Collapse.Panel>
                </Collapse>
                {
                    !addressActive.length && (
                        <div className={classes.addressTimeline}>
                            {renderTimelineAddress('sender', <EnvironmentOutlined />, '#f39c0e')}
                            <Divider dashed type="vertical" className={classes.addressTimelineLine} />
                            {renderTimelineAddress('destination', <EnvironmentOutlined />, '#26ad61')}
                        </div>
                    )
                }
            </div>
        );
    };

    const renderPrimaryDetails = () => {
        return (
            <div className={`${classes.box} ${classes.collapseSection}`}>
                <Collapse
                    activeKey={primaryDetailsActive}
                    onChange={(keys) => setPrimaryDetailsActive(Array.isArray(keys) ? keys : [keys])}
                >
                    <Collapse.Panel
                        header={
                            (
                                <div>
                                    <ConsignmentsIcon />
                                    &nbsp;
                                    {t('primary_details')}
                                </div>
                            )
                        }
                        key="1"
                    >
                        <ConsignmentDetailsInternational
                            consignment={consignment}
                            selectedTab="primary_details"
                        />
                    </Collapse.Panel>
                </Collapse>
                {
                    !primaryDetailsActive.length && (
                        <div className={`${classes.primaryDetails} ${classes.quickDetails}`}>
                            <div className={classes.quickDetail}>
                                <div className={classes.grayText}>{t('load_type')}</div>
                                <div className={classes.boldText}>{consignment.load_type || 'N/A'}</div>
                            </div>
                            <div className={classes.quickDetail}>
                                <div className={classes.grayText}>{t('weight')}</div>
                                <div className={classes.boldText}>
                                    {consignment.weight ? `${consignment.weight} kg` : 'N/A'}
                                </div>
                            </div>
                            <div className={classes.quickDetail}>
                                <div className={classes.grayText}>{t('dimensions')}</div>
                                <div className={classes.boldText}>
                                    {
                                        consignment.length && consignment.width && consignment.height
                                            ? `${consignment.length} x ${consignment.width} x ${consignment.height} cm`
                                            : 'N/A'
                                    }
                                </div>
                            </div>
                            <div className={classes.quickDetail}>
                                <div className={classes.grayText}>{t('declaredValue')}</div>
                                <div className={classes.boldText}>{consignment.declared_value}</div>
                            </div>
                        </div>
                    )
                }
            </div>
        );
    };

    const renderSinglePiece = (piece: any, index: any) => {
        return (
            <div className={classes.pieceCard} key={index}>
                <div className={classes.pieceRow}>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <div
                            style={{
                                backgroundColor: '#9235e9', borderRadius: 15, color: '#fff', padding: '0 6px',
                            }}
                        >
                            {index + 1}
                        </div>
                        <div className={classes.boldText}>
                            {separateChildCN ? piece.reference_number : `Piece ${index + 1}`}
                        </div>
                    </div>
                    <div
                        className={classes.textBlock}
                        style={{
                            color: statusColorObj.color,
                            background: statusColorObj.background,
                            borderRadius: 15,
                        }}
                    >
                        <StatusPrettyName status={consignment.status} />
                    </div>
                </div>
                <div className={classes.pieceRow}>
                    <div className={classes.boldText}>
                        {piece.weight ? `${piece.weight} kg` : ''}
                    </div>
                    <div className={classes.grayText}>
                        {piece.height && piece.width && piece.length
                            ? `${piece.length} x ${piece.width} x ${piece.height} cm` : ''}
                    </div>
                </div>
                <div className={classes.pieceRow}>
                    <div className={classes.grayText}>{piece.description}</div>
                    <div className={classes.grayText}>
                        Items:
                        &nbsp;
                        <span className={classes.boldText}>
                            {piece.pieces_item_details?.length || 'NA'}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    const renderPiecesDetails = () => {
        if (!consignment.pieces_detail?.length) {
            return null;
        }
        return (
            <div className={`${classes.box} ${classes.tracking}`} style={{ padding: 0 }}>
                <div className={classes.boxHeader} style={{ padding: 15 }}>
                    <ConsignmentsIcon />
                    &nbsp; Pieces Details
                </div>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        backgroundColor: '#f9f1ff',
                        padding: 15,
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div className={`${classes.grayText} ${classes.boldText}`}>Total Pieces</div>
                        <div className={`${classes.purpleText} ${classes.boldText}`}>
                            {consignment.pieces_detail.length}
                            &nbsp;
                            Pieces
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div className={`${classes.grayText} ${classes.boldText}`}>Total Weight</div>
                        <div className={`${classes.grayText} ${classes.boldText}`}>
                            {(
                                consignment.pieces_detail.reduce((acc: any, cur: { weight: any; }) => {
                                    return acc + (cur.weight ? parseFloat(cur.weight) : 0);
                                }, 0).toFixed(2)
                            )}
                            &nbsp;
                            Kg
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div className={`${classes.grayText} ${classes.boldText}`}>Total Items</div>
                        <div className={`${classes.purpleText} ${classes.boldText}`}>
                            {consignment.pieces_detail.reduce((acc: any, cur: { pieces_item_details: any; }) => {
                                return acc + (cur.pieces_item_details?.length || 0);
                            }, 0)}
                            &nbsp;
                            Items
                        </div>
                    </div>
                </div>
                <div>
                    {
                        consignment.pieces_detail.map((piece: any, index: any) => (
                            renderSinglePiece(piece, index)
                        ))
                    }
                </div>
            </div>
        );
    };

    return (
        <Drawer
            visible
            width="100%"
            title={renderHeader()}
            closable={false}
            className={classes.drawer}
        >
            {
                loading ? <Loader zIndex={1000} />
                    : (
                        <div className={classes.main}>
                            {renderEdd()}
                            {renderQuickDetails()}
                            {renderTracking()}
                            {renderAddress()}
                            {renderPrimaryDetails()}
                            {renderPiecesDetails()}
                            {renderCompleteTracking()}
                        </div>
                    )
            }
        </Drawer>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    const { master } = state;
    const { parts_to_show } = master;
    const separateChildCN = parts_to_show?.separate_child_cns;
    const currencySymbol = master?.config?.currency_symbol || '';
    return {
        currencySymbol,
        separateChildCN,
    };
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: styles,
    },
    connectRouter: true,
};
export default GenericHoc(hocConfig)(ConsignmentDetailsMobile);
